import { useState, useEffect } from "react";
import axios from "axios";
import ERC20ABI from "../abis/erc20";
import tokens from "../libs/tokens.json";
import { AlertCircle, BookCopy, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";
import { formatUnits } from "ethers";

// Define the API base URL at the top
const baseUrl = `${process.env.REACT_APP_API_URL}/transaction`;

const TransactionWidget = ({ walletAddress }) => {
  const [transactions, setTransactions] = useState([]);
  const [totalInLastDay, setTotalInLastDay] = useState(0);
  const [loading, setLoading] = useState(false);

  // Pagination state:
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchTransactions = async (walletAddress, page) => {
    if (!walletAddress) return;

    setLoading(true);
    try {
      // Pass page as a query parameter
      const response = await axios.get(
        `${baseUrl}/${walletAddress}?page=${page}`
      );

      const {
        transactions: fetchedTxs,
        totalInLast24Hours,
        pagination,
      } = response.data;

      setTransactions(fetchedTxs);
      setTotalInLastDay(totalInLast24Hours);

      // Update pagination info
      setTotalPages(pagination.totalPages);
    } catch (error) {
      console.error("Error fetching transaction:", error);
    } finally {
      setLoading(false);
    }
  };

  // Whenever walletAddress or page changes, re-fetch
  useEffect(() => {
    if (walletAddress) {
      fetchTransactions(walletAddress, page);
    }
  }, [walletAddress, page]);

  // Handlers for Prev/Next buttons
  const handlePrev = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setPage((prev) => Math.min(prev + 1, totalPages));
  };

  const getTokenValue = (amount, id) => {
    const token = tokens.find((t) => t.id.toLowerCase() == id.toLowerCase());
    return token?.decimals ? formatUnits(amount, token?.decimals) : "-";
  };

  const getSymbolById = (id) => {
    const token = tokens.find((t) => t.id.toLowerCase() == id.toLowerCase());
    return token ? token.symbol : "";
  };

  return (
    <div className="backdrop-blur-md bg-white/70 dark:bg-slate-900/70 rounded-3xl shadow-2xl p-6 w-full mx-auto border border-emerald-100 dark:border-slate-700 min-h-[400px]">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-cyan-600">
          Transaction History
        </h2>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-teal-400">
          <div className="w-12 h-12 border-4 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="italic text-lg font-medium">Loading...</p>
        </div>
      ) : (
        <div className="order-list w-full space-y-5">
          <div className="order-item p-6 rounded-2xl border border-emerald-100 dark:border-slate-700 shadow-sm hover:shadow-lg transition-shadow overflow-x-auto">
            <table className="table-auto w-full min-w-[600px] text-left">
              <thead>
                <tr className="border-b border-teal-200 border-none">
                  {[
                    "Date",
                    "Pair",
                    "Loan Amount",
                    "Status",
                    "Transaction Hash",
                  ].map((header) => (
                    <th
                      key={header}
                      className="text-lg font-semibold text-teal-600 px-4 py-3"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {transactions?.length > 0 ? (
                  transactions.map((tx) => (
                    <tr
                      key={tx._id}
                      className="border-b last:border-b-0 border-emerald-100 dark:border-slate-700 hover:bg-emerald-500/5 hover:border-emerald-400/20 transition-colors duration-200"
                    >
                      <td className="text-white px-4 py-3 whitespace-nowrap">
                        {new Date(tx.createdAt).toLocaleString()}
                      </td>
                      <td className="text-white px-4 py-3 whitespace-nowrap font-medium">
                        {getSymbolById(tx.flashLoanToken)} -{" "}
                        {getSymbolById(tx.tokenOut)}
                      </td>
                      <td className="text-white px-4 py-3 whitespace-nowrap">
                        {getTokenValue(tx.flashLoanAmount, tx.flashLoanToken)}{" "}
                        {getSymbolById(tx.flashLoanToken)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`inline-block px-3 py-1.5 rounded-full text-sm font-semibold
                          ${
                            tx.status === "confirmed"
                              ? "bg-green-500/10 text-green-300 border border-green-600"
                              : tx.status === "pending"
                              ? "bg-yellow-400/10 text-yellow-300 border border-yellow-500"
                              : "bg-red-500/10 text-red-400 border border-red-600"
                          }`}
                        >
                          {tx.status.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap max-w-[180px] truncate">
                        <a
                          href={`https://sepolia.etherscan.io/tx/${tx.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="View on Etherscan"
                          className="text-teal-700 hover:text-cyan-600 font-mono text-sm block truncate"
                        >
                          {tx.txHash}
                        </a>
                        <button
                          onClick={() => {
                            toast.dismiss();
                            navigator.clipboard.writeText(tx.txHash);
                            toast.success("Transaction Hash Copied");
                          }}
                          aria-label="Copy transaction hash"
                          className="mt-1 text-cyan-500 hover:text-cyan-700 transition-colors"
                        >
                          <BookCopy className="inline h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5}>
                      <div className="backdrop-blur-md bg-white/70 dark:bg-slate-900/70 p-10 rounded-2xl text-center shadow-sm flex flex-col items-center justify-center gap-3 text-teal-400">
                        <AlertCircle className="h-12 w-12" />
                        <p className="text-xl font-semibold">
                          No Transaction History Available
                        </p>
                        <p className="text-sm text-teal-300">
                          Maybe your bot is inactive!
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {transactions.length > 0 && (
            <div className="flex justify-end items-center space-x-5 mt-6">
              <button
                onClick={handlePrev}
                disabled={page === 1}
                className={`p-3 rounded-lg transition-colors ${
                  page === 1
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="text-teal-700 font-medium">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={handleNext}
                disabled={page === totalPages}
                className={`p-3 rounded-lg transition-colors ${
                  page === totalPages
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
                aria-label="Next page"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionWidget;
